import React, { useCallback } from 'react';
import { _ } from '@splunk/ui-utils/i18n';
import Popover from '@splunk/react-ui/Popover';
import Menu from '@splunk/react-ui/Menu';
import { usePopoverBlurCallback } from '@splunk/dashboard-ui';

const menuStyle = { width: 130 };

export interface LayerMenuProps {
    open: boolean;
    itemIdx: number;
    adjustOrder: (obj: { from: number; to: number; event: string }) => void;
    front: number;
    onRequestClose: () => void;
    anchor: React.MutableRefObject<HTMLButtonElement | null>;
    layerMenuId: string;
}

export const LayerMenu = ({
    open,
    itemIdx,
    adjustOrder,
    front,
    onRequestClose,
    anchor,
    layerMenuId,
}: LayerMenuProps): JSX.Element | null => {
    const handleForwardClick = useCallback(
        () =>
            adjustOrder({
                from: itemIdx,
                to: itemIdx + 1,
                event: 'layer-forward',
            }),
        [adjustOrder, itemIdx]
    );

    const handleFrontClick = useCallback(
        () =>
            adjustOrder({
                from: itemIdx,
                to: front,
                event: 'layer-front',
            }),
        [adjustOrder, itemIdx, front]
    );

    const handleBackwardClick = useCallback(
        () =>
            adjustOrder({
                from: itemIdx,
                to: itemIdx - 1,
                event: 'layer-backward',
            }),
        [adjustOrder, itemIdx]
    );

    const handleBackClick = useCallback(
        () =>
            adjustOrder({
                from: itemIdx,
                to: 0,
                event: 'layer-back',
            }),
        [adjustOrder, itemIdx]
    );

    const onBlur = usePopoverBlurCallback(onRequestClose);

    const isFront = itemIdx === front;
    const isBack = itemIdx === 0;

    return (
        <Popover
            open={open}
            anchor={anchor.current}
            onRequestClose={onRequestClose}
            onBlur={onBlur}
            defaultPlacement="right"
            appearance="normal"
            takeFocus
            id={layerMenuId}
        >
            <Menu style={menuStyle} data-test="layer-menu">
                {!isFront && (
                    <Menu.Item
                        data-test="layer-forward"
                        onClick={handleForwardClick}
                    >
                        {_('Move forward')}
                    </Menu.Item>
                )}
                {!isFront && (
                    <Menu.Item
                        data-test="layer-front"
                        onClick={handleFrontClick}
                    >
                        {_('Bring to front')}
                    </Menu.Item>
                )}
                {!isBack && (
                    <Menu.Item
                        data-test="layer-backward"
                        onClick={handleBackwardClick}
                    >
                        {_('Move backward')}
                    </Menu.Item>
                )}
                {!isBack && (
                    <Menu.Item data-test="layer-back" onClick={handleBackClick}>
                        {_('Send to back')}
                    </Menu.Item>
                )}
            </Menu>
        </Popover>
    );
};
